
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import SelecionarNcm from '@/components/Cadastros/Produtos/SelecionarNcm.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import ServicoNcm from '@/servicos/Cadastros/Produtos/ServicoNcm';
import { INcm } from '@/models/Entidades/Cadastros/Produtos/INcm';
import storeSistema from '@/store/storeSistema';
import ServicoPAS from '@/servicos/ServicoPAS';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { EStatusCuradoriaCadastro } from '@/models/Enumeradores/PainelAdministrativo/EStatusCuradoriaCadastro';
import { ETipoAmbiente } from '@/models/Enumeradores/PainelAdministrativo/ETipoAmbiente';

export default defineComponent({
  name: 'NcmModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
    ncmCuradoria: {
      type: Object as () => INcm,
    },
  },
  components: {
    Icone,
    RequisicaoModal,
    SelecionarNcm,
  },
  emits: ['sincronizarRegistro', 'acaoCuradoria', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes,
      preencherPermissoesDados, filtrarPermissaoDadosUsuario, apresentarMensagemSucesso,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso,
      defineNovaOperacao, defineTextoPadraoBotoes, sincronizarRegistro,
    } = useModalBase(props, emit);

    const servicoNcm = new ServicoNcm();
    const servicoPAS = new ServicoPAS();
    telaBase.identificadorRecurso = 'CADASTRO_NCMS';
    telaBase.identificadorPermissao = 'PER_CADASTRO_NCMS';

    const state = reactive({
      ncm: {} as INcm,
      aplicarRegrasEnvioCuradoria: false,
      aplicarRegrasRevisaoCuradoria: false,
    });

    function objetoInicial() {
      state.ncm = {} as INcm;
      state.ncm.codigo = 0;
      state.aplicarRegrasEnvioCuradoria = false;
      state.aplicarRegrasRevisaoCuradoria = false;
    }

    function aprovarCadastro() {
      emit('acaoCuradoria', EStatusCuradoriaCadastro.Aprovado, state.ncm);
      modalBase.computedVisivel = false;
    }

    function reprovarCadastro() {
      emit('acaoCuradoria', EStatusCuradoriaCadastro.Reprovado, state.ncm);
      modalBase.computedVisivel = false;
    }

    async function salvar(salvarNovo: boolean) {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      apresentarBarraProgresso();

      if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Administracao) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          retorno = await servicoNcm.incluir(state.ncm);
        } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          retorno = await servicoNcm.alterar(state.ncm);
        }

        ocultarBarraProgresso();

        if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
          if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
            sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
          } else {
            sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
          }
          apresentarMensagemSucesso(retorno.mensagem);
          if (salvarNovo) {
            objetoInicial();
            defineNovaOperacao(props.operacao);
          } else {
            modalBase.computedVisivel = false;
          }
        } else {
          apresentarRetornoRequisicao(retorno);
        }
      } else if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Plataforma) {
        retorno = await servicoPAS.solicitarCadastroCuradoria(telaBase.identificadorRecurso, state.ncm);

        ocultarBarraProgresso();

        if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
          apresentarMensagemSucesso(retorno.mensagem);
          modalBase.computedVisivel = false;
        } else {
          apresentarRetornoRequisicao(retorno);
        }
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();
      if (modalBase.computedVisivel) {
        if (UtilitarioGeral.objetoValido(props.ncmCuradoria)) {
          state.aplicarRegrasRevisaoCuradoria = true;
          state.ncm = UtilitarioGeral.instanciaObjetoLocal(props.ncmCuradoria);
        } else {
          if (props.operacao.listaPermissoesDados.length > 0) {
            await preencherPermissoesDados(props.operacao.listaPermissoesDados);
          } else {
            await obterPermissoes(ETipoPermissao.Dados);
          }
          telaBase.permissaoDados = filtrarPermissaoDadosUsuario(storeSistema.getters.codigoEmpresaOperacao());

          if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Administracao) {
            state.aplicarRegrasEnvioCuradoria = false;
            defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
          } else if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Plataforma) {
            modalBase.textoBotaoSalvar = 'Sugerir Cadastro';
            state.aplicarRegrasEnvioCuradoria = true;
          }

          if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
            state.ncm = await servicoNcm.obter(props.operacao.codigoRegistro, storeSistema.getters.codigoEmpresaOperacao());
          }
        }
      }
      telaBase.carregando = false;
    });

    return {
      telaBase,
      props,
      modalBase,
      state,
      salvar,
      objetoInicial,
      EPermissaoDados,
      aprovarCadastro,
      reprovarCadastro,
    };
  },
});
